import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Ticket, X, ArrowRight } from "lucide-react";

/**
 * Sticky top banner that surfaces a pending coupon code while the user
 * shops. It activates when:
 *   • a `?coupon=CODE` URL param is present anywhere on the site, OR
 *   • a previously-seen pending coupon is stored in sessionStorage.
 *
 * The banner stays visible on /shop, /cart, and product pages — and
 * disappears on /checkout (Checkout consumes & applies the coupon).
 * Users can dismiss it with the X — which clears sessionStorage too.
 */
const STORAGE_KEY = "pending_coupon";
const DISMISS_KEY = "pending_coupon_dismissed";

export default function PendingCouponBanner() {
  const location = useLocation();
  const navigate = useNavigate();
  const [code, setCode] = useState("");

  // Sync URL param → sessionStorage on every route change
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlCode = params.get("coupon");
    queueMicrotask(() => {
      if (urlCode) {
        sessionStorage.setItem(STORAGE_KEY, urlCode);
        sessionStorage.removeItem(DISMISS_KEY);
        setCode(urlCode);
        return;
      }
      const stored = sessionStorage.getItem(STORAGE_KEY);
      setCode(stored && !sessionStorage.getItem(DISMISS_KEY) ? stored : "");
    });
  }, [location.pathname, location.search]);

  // Don't show on checkout (Checkout itself displays & applies) or on
  // auth pages where it would feel intrusive.
  const hidden = /^\/(checkout|login|signup|register|admin)/.test(location.pathname) || !code;
  if (hidden) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setCode("");
  };
  const goCheckout = () => {
    navigate(`/checkout?coupon=${encodeURIComponent(code)}`);
  };

  return (
    <div
      className="sticky top-0 z-40 bg-gradient-to-r from-[#FF5722] via-pink-500 to-purple-600 text-white animate-in slide-in-from-top duration-300"
      data-testid="pending-coupon-banner"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2 flex items-center gap-2 sm:gap-4">
        <div className="hidden sm:flex flex-shrink-0 w-8 h-8 rounded-lg bg-white/20 backdrop-blur-sm border border-white/30 items-center justify-center">
          <Ticket className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0 text-xs sm:text-sm flex flex-wrap items-center gap-x-2">
          <span className="font-bold whitespace-nowrap">🎟️ Coupon ready:</span>
          <span className="font-mono font-black tracking-wider bg-white/15 px-2 py-0.5 rounded text-xs">{code}</span>
          <span className="hidden md:inline opacity-90">— add items to cart and we&apos;ll apply it at checkout.</span>
          <span className="md:hidden opacity-90">— add items to apply.</span>
        </div>
        <button
          type="button"
          onClick={goCheckout}
          data-testid="pending-coupon-checkout-cta"
          className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 bg-white text-[#FF5722] font-bold text-xs rounded-md hover:bg-zinc-100 transition-colors whitespace-nowrap"
        >
          Checkout <ArrowRight className="w-3 h-3" />
        </button>
        <button
          type="button"
          onClick={dismiss}
          data-testid="pending-coupon-dismiss"
          aria-label="Dismiss"
          className="text-white/80 hover:text-white transition-colors flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
